import { Flex, SimpleGrid } from "@mantine/core";

import MultiFieldCard from "../../../../components/Cards/MultiFieldCard/MultiFieldCard.tsx";
import SingleFieldCard from "../../../../components/Cards/SingleFieldCard/SingleFieldCard.tsx";
import { DatabaseMetadataPopover } from "../../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import { ClikResult } from "../../../../types/clikData/clikResult.ts";
import { toLocalDate } from "../../../../utils/date.ts";
import ClikResultAccountReceivables from "../ClikResultAccountReceivables/ClikResultAccountReceivables.tsx";
import ClikResultAdditionalInformation from "../ClikResultAdditionalInformation/ClikResultAdditionalInformation.tsx";
import ClikResultAddresses from "../ClikResultAddresses/ClikResultAddresses.tsx";
import ClikResultCbScore from "../ClikResultCbScore/ClikResultCbScore.tsx";
import ClikResultContacts from "../ClikResultContacts/ClikResultContacts.tsx";
import ClikResultBondContracts from "../ClikResultContractDetails/ClikResultBondContracts/ClikResultBondContracts.tsx";
import ClikResultCreditContracts from "../ClikResultContractDetails/ClikResultCreditContracts/ClikResultCreditContracts.tsx";
import ClikResultGuaranteeContracts from "../ClikResultContractDetails/ClikResultGuaranteeContracts/ClikResultGuaranteeContracts.tsx";
import ClikResultLetterOfCreditContracts from "../ClikResultContractDetails/ClikResultLetterOfCreditContracts/ClikResultLetterOfCreditContracts.tsx";
import ClikResultOtherFacilitiesContracts from "../ClikResultContractDetails/ClikResultOtherContracts/ClikResultOtherContracts.tsx";
import ClikResultContractSummary from "../ClikResultContractSummary/ClikResultContractSummary.tsx";
import ClikResultEmployment from "../ClikResultEmployment/ClikResultEmployment.tsx";
import ClikResultFootprint from "../ClikResultFootprint/ClikResultFootprint.tsx";
import ClikResultNegativeEvent from "../ClikResultNegativeEvent/ClikResultNegativeEvent.tsx";
import { getCRSubjectMatched } from "../utils.ts";
import { useCRIndividualResultInfoCards } from "./hooks/useCRIndividualResultInfoCards.tsx";

interface ClikResultIndividualProps {
  clikResult: ClikResult;
}

const ClikResultIndividual = ({ clikResult }: ClikResultIndividualProps) => {
  const { subject, indentificationDocuments, applicationSummary } =
    useCRIndividualResultInfoCards({ clikResult });

  return (
    <Flex gap={{ base: 20, sm: 16 }} direction="column">
      <Flex gap={{ base: 20, sm: 16 }} direction="column">
        <SimpleGrid
          cols={4}
          breakpoints={[{ maxWidth: "lg", cols: 1 }]}
          spacing={20}
          verticalSpacing={16}
        >
          <SingleFieldCard
            label={
              <DatabaseMetadataPopover
                databaseMetadata={{
                  tableName: "ClikResult",
                  fieldName: "createdAt",
                }}
                position="right"
              >
                Request Date
              </DatabaseMetadataPopover>
            }
            value={clikResult.createdAt && toLocalDate(clikResult.createdAt)}
            variant="blue"
          />
          <SingleFieldCard
            label={
              <DatabaseMetadataPopover
                databaseMetadata={{
                  tableName: "ClikResult",
                  fieldName: "isSubjectMatched",
                }}
                position="right"
              >
                Status
              </DatabaseMetadataPopover>
            }
            value={getCRSubjectMatched(clikResult.isSubjectMatched)}
            variant="blue"
          />
          <SingleFieldCard
            label={
              <DatabaseMetadataPopover
                databaseMetadata={{
                  tableName: "ClikIndividualMatchedSubject",
                  fieldName: "npwpId",
                }}
                position="right"
              >
                Identity Number
              </DatabaseMetadataPopover>
            }
            value={clikResult.individualSubject?.npwpId}
            variant="blue"
          />

          <SingleFieldCard
            label={
              <DatabaseMetadataPopover
                databaseMetadata={{
                  tableName: "ClikData",
                  fieldName: "referenceNumber",
                }}
                position="right"
              >
                Reference Number
              </DatabaseMetadataPopover>
            }
            value={clikResult.clikData?.referenceNumberV2}
            variant="blue"
          />
        </SimpleGrid>
        <SimpleGrid
          cols={3}
          breakpoints={[{ maxWidth: "lg", cols: 1 }]}
          spacing={20}
          verticalSpacing={16}
        >
          <MultiFieldCard label="Subject Data" items={subject} variant="blue" />
          <MultiFieldCard
            label="Identification Documents"
            items={indentificationDocuments}
            variant="blue"
          />
          <MultiFieldCard
            label="Application Summary"
            items={applicationSummary}
            variant="blue"
          />
        </SimpleGrid>
      </Flex>
      <ClikResultAddresses clikResultId={clikResult.id} />
      <ClikResultEmployment subjectId={clikResult.individualSubject?.id} />
      <ClikResultContacts clikResultId={clikResult.id} />
      <ClikResultCbScore clikResult={clikResult} />
      <ClikResultContractSummary clikResultId={clikResult.id} />
      <ClikResultCreditContracts clikResultId={clikResult.id} />
      <ClikResultBondContracts clikResultId={clikResult.id} />
      <ClikResultGuaranteeContracts clikResultId={clikResult.id} />
      <ClikResultLetterOfCreditContracts clikResultId={clikResult.id} />
      <ClikResultOtherFacilitiesContracts clikResultId={clikResult.id} />
      <ClikResultAccountReceivables clikResultId={clikResult.id} />
      <ClikResultAdditionalInformation
        additionalInfo={clikResult.individualSubject?.additionalInfo}
      />
      <ClikResultNegativeEvent clikResultId={clikResult.id} />
      <ClikResultFootprint footprintsStatistic={clikResult} />
    </Flex>
  );
};

export default ClikResultIndividual;
